import { bindClickWheelZoom } from "../utils/clickWheelZoom";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box, Button, Paper, Stack, ToggleButton, ToggleButtonGroup, Typography, useTheme,
} from "@mui/material";
import {
  MapContainer, TileLayer, Marker, Polygon, Polyline, CircleMarker, Tooltip as MapTooltip, useMap, useMapEvents,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import AdaptiveSatelliteLayer from "./AdaptiveSatelliteLayer";
import { IMAGERY_MAX_ZOOM, isArcGisTileUrl } from "../utils/adaptiveImagery";

import { ringAreaM2, describeArea, ringCentre, ringEdges } from "../utils/parcelGeometry";

/**
 * Click-to-pick site coordinates, and draw the parcel around them.
 *
 * The provider lives behind two environment variables, never a literal. Swapping
 * OpenStreetMap for a paid basemap is then a deploy config change rather than a
 * code change -- which matters, because OSM's tile policy warns that commercial
 * services "may no longer be able to serve your paying customers if access is
 * withdrawn". Attribution has to travel with the URL: every commercial provider
 * requires their name on the map, so the two are set together or not at all.
 *
 * THE PARCEL
 * ----------
 * A site used to be a point. It is now also a boundary: in "Parcel" mode each
 * click adds a vertex, vertices drag, a right-click removes one, and the shape
 * is stored on the project as GeoJSON. Everything that follows from the shape
 * -- how many rows fit, how long each is, how the ground slopes under them --
 * is worked out on the server from that polygon; the map only draws it and
 * reports the area so the person drawing can see what they have enclosed.
 *
 * Aerial imagery is offered because a boundary is drawn against fences, tracks
 * and field edges, none of which a street map shows.
 */
const TILE_URL =
  process.env.REACT_APP_MAP_TILE_URL || "https://tile.openstreetmap.org/{z}/{x}/{y}.png";
const TILE_ATTRIBUTION =
  process.env.REACT_APP_MAP_TILE_ATTRIBUTION ||
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';
const SATELLITE_URL =
  process.env.REACT_APP_MAP_SATELLITE_URL ||
  "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}";
const SATELLITE_ATTRIBUTION =
  process.env.REACT_APP_MAP_SATELLITE_ATTRIBUTION ||
  "Tiles &copy; Esri &mdash; Source: Esri, Maxar, Earthstar Geographics, and the GIS User Community";

// Somewhere sensible to look at before a project has coordinates.
const FALLBACK = { lat: 37.98, lon: 23.73, zoom: 6 };
const PICKED_ZOOM = 13;
const PARCEL_ZOOM = 16;

/**
 * Six decimals is about 11 cm. More is noise, and every extra digit is another
 * spurious change that would re-trigger the timezone lookup downstream.
 */
const round6 = (n) => Math.round(Number(n) * 1e6) / 1e6;

const isCoord = (lat, lon) =>
  Number.isFinite(Number(lat)) &&
  Number.isFinite(Number(lon)) &&
  Math.abs(Number(lat)) <= 90 &&
  Math.abs(Number(lon)) <= 180;

/**
 * The marker.
 *
 * Leaflet's default icon is a PNG resolved relative to its own stylesheet, which
 * webpack rewrites -- the classic broken-marker bug in a Create React App build.
 * Drawing it inline avoids the asset plumbing entirely and lets the pin take the
 * MUI primary colour instead of Leaflet's blue.
 */
const pinIcon = (color) =>
  L.divIcon({
    className: "",
    html: `
      <svg width="26" height="34" viewBox="0 0 26 34" xmlns="http://www.w3.org/2000/svg">
        <path d="M13 33C13 33 24.5 20.9 24.5 13A11.5 11.5 0 1 0 1.5 13C1.5 20.9 13 33 13 33Z"
              fill="${color}" stroke="#fff" stroke-width="2"
              style="filter: drop-shadow(0 2px 3px rgba(0,0,0,.35))"/>
        <circle cx="13" cy="13" r="4.2" fill="#fff"/>
      </svg>`,
    iconSize: [26, 34],
    iconAnchor: [13, 33],
  });

function ClickWheelZoom() {
  const map = useMap();
  useEffect(() => bindClickWheelZoom(map.getContainer(), (active) => {
    if (active) map.scrollWheelZoom.enable();
    else map.scrollWheelZoom.disable();
  }), [map]);
  return null;
}

/** Turns a map click into a coordinate, or a vertex, depending on the mode. */
function ClickHandler({ mode, onPick, onVertex, onFinish }) {
  useMapEvents({
    click(e) {
      // wrap() folds longitude back into [-180, 180]: panning across the date
      // line otherwise yields values like 190, which the backend would reject.
      const { lat, lng } = e.latlng.wrap();
      if (mode === "parcel") onVertex(round6(lng), round6(lat));
      else onPick(round6(lat), round6(lng));
    },
    dblclick(e) {
      if (mode === "parcel") {
        L.DomEvent.stop(e);
        onFinish();
      }
    },
  });
  return null;
}

/**
 * Keeps the map in step with the numeric fields, without fighting the user.
 *
 * Typing coordinates should move the map. Clicking the map should NOT then move
 * it again -- the click already put the pin where the user wanted it, and
 * re-centring on every pick makes the map creep under the cursor. So a pick is
 * remembered and the matching prop update is ignored.
 */
function SyncView({ lat, lon, lastPickedRef }) {
  const map = useMap();
  useEffect(() => {
    if (!isCoord(lat, lon)) return;
    const picked = lastPickedRef.current;
    if (picked && picked.lat === Number(lat) && picked.lon === Number(lon)) return;
    const zoom = map.getZoom() < PICKED_ZOOM ? PICKED_ZOOM : map.getZoom();
    map.setView([Number(lat), Number(lon)], zoom, { animate: true });
  }, [lat, lon, map, lastPickedRef]);
  return null;
}

/** Zooms in once when parcel drawing starts: a boundary needs field edges. */
function ZoomForDrawing({ mode }) {
  const map = useMap();
  useEffect(() => {
    if (mode === "parcel" && map.getZoom() < PARCEL_ZOOM) {
      map.setZoom(PARCEL_ZOOM, { animate: true });
    }
    // Double-click zoom would fight "double-click to finish".
    if (mode === "parcel") map.doubleClickZoom.disable();
    else map.doubleClickZoom.enable();
  }, [mode, map]);
  return null;
}

/**
 * Leaflet measures its container once, at mount. Inside a collapsed section --
 * and Site Location is collapsible -- that measurement is zero, and the map
 * renders as a grey box with one tile in the corner when the panel opens. A
 * ResizeObserver re-measures whenever the container actually changes size,
 * which covers the panel opening, the window resizing and the drawer toggling.
 */
function InvalidateOnResize() {
  const map = useMap();
  useEffect(() => {
    const el = map.getContainer();
    const ro = new ResizeObserver(() => map.invalidateSize());
    ro.observe(el);
    const t = setTimeout(() => map.invalidateSize(), 0);
    return () => {
      ro.disconnect();
      clearTimeout(t);
    };
  }, [map]);
  return null;
}

export default function LocationPicker({
  lat, lon, onChange, height = 420,
  parcel = null,              // open ring [[lon, lat], ...] or null
  onParcelChange = null,      // (ring | null) => void
  rows = null,                // optional overlay: [{start_lonlat, end_lonlat}] tables to draw
  rowDepthM = null,           // the rows' ground footprint across the row, metres: drawn as
                              // blocks of that depth when known, as centre lines otherwise
  rowAzimuthDeg = 180,        // the side the rows face, for the depth offset
  readOnly = false,           // show, do not edit: no picking, no dragging, no toolbar
  onApplyParcel = null,       // () => void: take area and slope from the parcel
  applyBusy = false,
  applyStatus = "",           // one line under the toolbar after applying, or an error
  applyError = false,
  // Outlines drawn for reference and never edited: the plant boundary and the
  // other planes' fields while one plane's field is being traced.
  // [{ ring: [[lon, lat], ...], label, muted }]
  referenceRings = null,
  // What the toolbar calls the ring being drawn. The defaults are the
  // catalogue's keys; a caller drawing something else passes finished
  // sentences in the screen's language.
  ringLabels = null,
  // A field-only map: no pin to place, drawing from the start.
  drawOnly = false,
}) {
  const labels = {
    draw: "Draw parcel", edit: "Edit parcel", clear: "Clear parcel", area: "Parcel",
    ...(ringLabels && typeof ringLabels === "object" ? ringLabels : {}),
  };
  const theme = useTheme();
  const lastPickedRef = useRef(null);
  const valid = isCoord(lat, lon);
  const center = valid ? [Number(lat), Number(lon)] : [FALLBACK.lat, FALLBACK.lon];
  const canDraw = !readOnly && typeof onParcelChange === "function";

  const [mode, setMode] = useState(drawOnly ? "parcel" : "pin");
  const [satellite, setSatellite] = useState(Boolean(parcel));
  const [fullscreen, setFullscreen] = useState(false);
  const frameRef = useRef(null);

  // Full screen through the browser's own API on the frame that holds both
  // the toolbar and the map, so the controls travel with it. The map notices
  // the new size through its ResizeObserver; Esc leaves as usual.
  useEffect(() => {
    const onChange = () => setFullscreen(document.fullscreenElement === frameRef.current);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);
  const toggleFullscreen = () => {
    const el = frameRef.current;
    if (!el) return;
    if (document.fullscreenElement === el) document.exitFullscreen?.();
    else el.requestFullscreen?.();
  };
  useEffect(() => {
    const frame = frameRef.current;
    if (!fullscreen || !frame) return undefined;
    // Leaflet consumes wheel events over the map. Elsewhere in this full-screen
    // frame, the wheel must not scroll the document behind the toolbar.
    const stopScroll = (event) => event.preventDefault();
    frame.addEventListener("wheel", stopScroll, { passive: false });
    return () => frame.removeEventListener("wheel", stopScroll);
  }, [fullscreen]);
  // The ring being drawn. While open (mode === "parcel") clicks append to it;
  // it is committed to the project on every change so nothing is lost if the
  // user wanders off, but the "not yet a polygon" state is drawn as a line.
  const ring = useMemo(() => (Array.isArray(parcel) ? parcel : []), [parcel]);

  const handlePick = useCallback(
    (nextLat, nextLon) => {
      if (readOnly) return;
      lastPickedRef.current = { lat: nextLat, lon: nextLon };
      if (typeof onChange === "function") onChange(nextLat, nextLon);
    },
    [onChange, readOnly]
  );

  const addVertex = useCallback(
    (lonV, latV) => {
      if (!canDraw) return;
      onParcelChange([...ring, [lonV, latV]]);
    },
    [canDraw, onParcelChange, ring]
  );

  const moveVertex = useCallback(
    (index, lonV, latV) => {
      if (!canDraw) return;
      const next = ring.map((p, i) => (i === index ? [lonV, latV] : p));
      onParcelChange(next);
    },
    [canDraw, onParcelChange, ring]
  );

  const removeVertex = useCallback(
    (index) => {
      if (!canDraw) return;
      const next = ring.filter((_, i) => i !== index);
      onParcelChange(next.length ? next : null);
    },
    [canDraw, onParcelChange, ring]
  );

  const clearParcel = () => {
    if (!canDraw) return;
    onParcelChange(null);
    if (!drawOnly) setMode("pin");
  };

  const finishDrawing = () => { if (!drawOnly) setMode("pin"); };

  const areaM2 = useMemo(() => ringAreaM2(ring), [ring]);

  // MapContainer ignores prop changes after mount by design, so the initial
  // centre is captured once and every later move goes through SyncView.
  const initialCenter = useMemo(
    () => (ring.length >= 3 ? ringCentre(ring) : center),
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const accent = theme.palette.warning.main;
  const primary = theme.palette.primary.main;

  return (
    <Box
      ref={frameRef}
      sx={{
        // In full screen the frame is the whole viewport: give it the app's
        // surface behind the toolbar so the controls do not float on black.
        "&:fullscreen": {
          bgcolor: "background.paper", p: 1, boxSizing: "border-box",
          width: "100%", height: "100%", display: "flex", flexDirection: "column",
          overflow: "hidden", overscrollBehavior: "none", minHeight: 0,
        },
      }}
    >
      {canDraw && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75, flexWrap: "wrap", flexShrink: 0 }} useFlexGap>
          <ToggleButtonGroup size="small" exclusive value={mode} onChange={(_, v) => v && setMode(v)}>
            {!drawOnly ? (
              <ToggleButton value="pin" sx={{ textTransform: "none", px: 1.25 }}>Place pin</ToggleButton>
            ) : null}
            <ToggleButton value="parcel" sx={{ textTransform: "none", px: 1.25 }}>
              {ring.length ? labels.edit : labels.draw}
            </ToggleButton>
          </ToggleButtonGroup>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={satellite ? "aerial" : "map"}
            onChange={(_, v) => v && setSatellite(v === "aerial")}
          >
            <ToggleButton value="map" sx={{ textTransform: "none", px: 1.25 }}>Map</ToggleButton>
            <ToggleButton value="aerial" sx={{ textTransform: "none", px: 1.25 }}>Aerial</ToggleButton>
          </ToggleButtonGroup>
          <Button size="small" onClick={toggleFullscreen} sx={{ textTransform: "none" }}>
            {fullscreen ? "Exit full screen" : "Full screen"}
          </Button>
          {ring.length > 0 && (
            <Button size="small" color="warning" onClick={clearParcel} sx={{ textTransform: "none" }}>
              {labels.clear}
            </Button>
          )}
          <Box sx={{ flex: 1 }} />
          {ring.length >= 3 && (
            <Typography variant="caption" sx={{ fontWeight: 700 }}>
              {labels.area} {describeArea(areaM2)} · {ring.length} corners
            </Typography>
          )}
          {ring.length >= 3 && onApplyParcel && (
            <Button
              size="small"
              variant="outlined"
              onClick={onApplyParcel}
              disabled={applyBusy}
              sx={{ textTransform: "none", py: 0.25, px: 1.1, minHeight: 0, lineHeight: 1.4, fontSize: 12.5 }}
            >
              {applyBusy ? "Applying…" : "Apply parcel to site"}
            </Button>
          )}
        </Stack>
      )}

      <Paper
        variant="outlined"
        sx={{
          overflow: "hidden",
          borderRadius: 1,
          flex: fullscreen ? "1 1 0" : undefined,
          minHeight: 0,
          height: fullscreen ? undefined : height,
          "& .leaflet-container": {
            height: "100%",
            width: "100%",
            cursor: "crosshair",
            fontFamily: "inherit",
            backgroundColor: "action.hover",
          },
          // Leaflet ships its own chrome; align it with the surrounding MUI.
          "& .leaflet-control-attribution": {
            fontSize: 10,
            background: "rgba(255,255,255,.75)",
          },
          "& .leaflet-bar a": { color: "text.primary" },
        }}
      >
        <MapContainer
          center={initialCenter}
          zoom={ring.length >= 3 ? PARCEL_ZOOM : valid ? PICKED_ZOOM : FALLBACK.zoom}
          scrollWheelZoom={false}
          worldCopyJump
          maxZoom={IMAGERY_MAX_ZOOM}
        >
          {/* Esri coverage varies locally: fall back per tile instead of
              enlarging level 18 everywhere. Custom providers keep their limit. */}
          {satellite && isArcGisTileUrl(SATELLITE_URL) ? (
            <AdaptiveSatelliteLayer url={SATELLITE_URL} attribution={SATELLITE_ATTRIBUTION} />
          ) : satellite ? (
            <TileLayer key="sat" url={SATELLITE_URL} attribution={SATELLITE_ATTRIBUTION}
                       maxNativeZoom={18} maxZoom={IMAGERY_MAX_ZOOM} />
          ) : (
            <TileLayer key="map" url={TILE_URL} attribution={TILE_ATTRIBUTION}
                       maxNativeZoom={19} maxZoom={IMAGERY_MAX_ZOOM} />
          )}
          <ClickWheelZoom />
          <ClickHandler mode={mode} onPick={handlePick} onVertex={addVertex} onFinish={finishDrawing} />
          <SyncView lat={lat} lon={lon} lastPickedRef={lastPickedRef} />
          <ZoomForDrawing mode={mode} />
          <InvalidateOnResize />

          {/* Boundaries that are only shown: the plant's parcel behind a
              plane's field, and the other planes' fields, so a field is
              traced inside the land and beside its neighbours. */}
          {(Array.isArray(referenceRings) ? referenceRings : [])
            .filter((r) => Array.isArray(r?.ring) && r.ring.length >= 3)
            .map((r, i) => (
              <Polygon
                key={`ref-${i}`}
                positions={r.ring.map(([lonV, latV]) => [latV, lonV])}
                pathOptions={{
                  color: r.muted ? theme.palette.text.secondary : primary,
                  weight: 1.5, dashArray: "6 4", fillOpacity: 0.03, interactive: false,
                }}
              >
                {r.label ? (
                  <MapTooltip permanent direction="center" opacity={0.85} className="parcel-edge-label">
                    {r.label}
                  </MapTooltip>
                ) : null}
              </Polygon>
            ))}
          {/* The parcel: a closed polygon once it has three vertices, a line
              while it is still being traced, and handles in edit mode. */}
          {ring.length >= 3 ? (
            <Polygon
              positions={ring.map(([lonV, latV]) => [latV, lonV])}
              pathOptions={{ color: accent, weight: 2, fillColor: accent, fillOpacity: satellite ? 0.12 : 0.08 }}
            />
          ) : ring.length >= 2 ? (
            <Polyline
              positions={ring.map(([lonV, latV]) => [latV, lonV])}
              pathOptions={{ color: accent, weight: 2, dashArray: "4 4" }}
            />
          ) : null}
          {/* The length of every edge, written on it. A boundary is read in
              metres -- "the north side is 180 m" -- and the number is what a
              surveyor's plan or the client's title deed can be checked against. */}
          {ring.length >= 2 &&
            ringEdges(ring, { closed: ring.length >= 3 }).map((edge) => (
              <Polyline
                key={`edge-${edge.index}`}
                positions={[
                  [ring[edge.index][1], ring[edge.index][0]],
                  [ring[(edge.index + 1) % ring.length][1], ring[(edge.index + 1) % ring.length][0]],
                ]}
                pathOptions={{ opacity: 0, weight: 8, interactive: false }}
              >
                <MapTooltip permanent direction="center" opacity={0.92} className="parcel-edge-label">
                  {`${Math.round(edge.lengthM).toLocaleString("en-GB")} m`}
                </MapTooltip>
              </Polyline>
            ))}
          {mode === "parcel" &&
            ring.map(([lonV, latV], index) => (
              <VertexHandle
                key={index}
                lat={latV}
                lon={lonV}
                color={accent}
                onMove={(la, lo) => moveVertex(index, lo, la)}
                onRemove={() => removeVertex(index)}
              />
            ))}

          {/* Rows fitted to the parcel, when the layout tab has them. Each
              table is drawn as the block of ground its modules cover -- the
              fit's line is the row's centre, the depth is the footprint across
              the row, offset towards and away from the facing side -- so at a
              close zoom the rows have their real size on the land rather than
              collapsing to a hairline. */}
          {Array.isArray(rows) &&
            rows.map((table, i) => {
              if (!table?.start_lonlat || !table?.end_lonlat) return null;
              const a = [table.start_lonlat[1], table.start_lonlat[0]];
              const b = [table.end_lonlat[1], table.end_lonlat[0]];
              const depth = Number(rowDepthM);
              if (!(depth > 0)) {
                return (
                  <Polyline key={`row-${i}`} positions={[a, b]}
                            pathOptions={{ color: primary, weight: 3, opacity: 0.9 }} />
                );
              }
              const az = (Number(rowAzimuthDeg) || 180) * (Math.PI / 180);
              const half = depth / 2;
              const dLat = (half * Math.cos(az)) / 111320;
              const dLon = (half * Math.sin(az)) / (111320 * Math.cos((a[0] * Math.PI) / 180));
              return (
                <Polygon
                  key={`row-${i}`}
                  positions={[
                    [a[0] + dLat, a[1] + dLon], [b[0] + dLat, b[1] + dLon],
                    [b[0] - dLat, b[1] - dLon], [a[0] - dLat, a[1] - dLon],
                  ]}
                  pathOptions={{ color: primary, weight: 1, opacity: 0.95, fillColor: primary, fillOpacity: 0.55 }}
                />
              );
            })}

          {valid && (
            <MarkerPin
              lat={Number(lat)}
              lon={Number(lon)}
              onMove={handlePick}
              draggable={!readOnly && mode === "pin"}
            />
          )}
        </MapContainer>
      </Paper>

      {applyStatus ? (
        <Typography variant="caption" sx={{ mt: 0.75, display: "block", flexShrink: 0, color: applyError ? "error.main" : "text.secondary" }}>
          {applyStatus}
        </Typography>
      ) : null}
      {readOnly ? null : (
      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: "block", flexShrink: 0 }}>
        {mode === "parcel"
          ? "Click to add a corner, drag a corner to move it, right-click a corner to remove it. Double-click when done."
          : canDraw
            ? "Click the map or drag the pin to set the coordinates. Choose Draw parcel to trace the site boundary."
            : "Click the map or drag the pin to set the coordinates. Scroll to zoom."}
      </Typography>
      )}
    </Box>
  );
}

/** A draggable corner of the parcel. Right-click removes it. */
function VertexHandle({ lat, lon, color, onMove, onRemove }) {
  return (
    <CircleMarker
      center={[lat, lon]}
      radius={6}
      pathOptions={{ color: "#fff", weight: 2, fillColor: color, fillOpacity: 1 }}
      // Paths bubble mouse events to the map by default, so without this a
      // click on a corner would also add a new corner underneath it.
      bubblingMouseEvents={false}
      eventHandlers={{
        click: (e) => L.DomEvent.stop(e),
        contextmenu: (e) => {
          L.DomEvent.stop(e);
          onRemove();
        },
        // CircleMarker is not draggable in Leaflet; a mousedown starts a manual
        // drag on the map until mouseup. Kept minimal on purpose.
        mousedown: (e) => {
          const map = e.target._map;
          if (!map) return;
          L.DomEvent.stop(e);
          map.dragging.disable();
          const move = (ev) => {
            const { lat: la, lng: lo } = ev.latlng.wrap();
            e.target.setLatLng([la, lo]);
          };
          const up = (ev) => {
            map.off("mousemove", move);
            map.off("mouseup", up);
            map.dragging.enable();
            const { lat: la, lng: lo } = e.target.getLatLng().wrap();
            onMove(round6(la), round6(lo));
          };
          map.on("mousemove", move);
          map.on("mouseup", up);
        },
      }}
    />
  );
}

/**
 * Separated so the icon is rebuilt when the theme colour changes.
 *
 * Taken from the theme object, not from a CSS variable: this app builds its
 * palette with createTheme rather than the CSS-variables provider, so
 * --mui-palette-primary-main does not exist and a probe for it would fall back
 * to MUI's default blue -- wrong in light mode (#0B4DA2) and wrong in dark
 * (#4CAF84). Through the theme the pin simply follows the palette.
 */
function MarkerPin({ lat, lon, onMove, draggable = true }) {
  const theme = useTheme();
  const color = theme.palette.primary.main;
  const icon = useMemo(() => pinIcon(color), [color]);
  return (
    <Marker
      position={[lat, lon]}
      icon={icon}
      draggable={draggable}
      eventHandlers={{
        dragend: (e) => {
          const { lat: dLat, lng: dLon } = e.target.getLatLng().wrap();
          onMove(round6(dLat), round6(dLon));
        },
      }}
    />
  );
}
